import React from 'react';
import { useGate } from 'effector-react';
import type { GeneralID, HotspotGroups } from '@types';
import useTourEditing from '@hooks/useTourEditing';
import Title from '@components/Title';
import { $sceneMapApi } from '@stores/dataStores';
import { Collapse } from '@components/Collapse';
import ColorPicker from '@components/ColorPicker';
import { $GateGroupStyle } from '@stores/editingHotspotStores';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import PanelBase from '../../panel/PanelBase';

type StyleData = Pick<
  HotspotGroups,
  'bgColor' | 'fontColor' | 'activeBgColor' | 'activeFontColor'
>;

interface Props {
  sceneId: GeneralID;
  data: HotspotGroups;
  onRequestClose: () => void;
}

const items: DropdownItem<0 | 1>[] = [
  { id: 0, name: '平移切换' },
  { id: 1, name: '缩小场景进行分组切换' },
];

export function PanelGroupStyle({ sceneId, data, onRequestClose }: Props) {
  const { state, setState, defaultState, isChanged } = useTourEditing(data, {
    bgColor: '0,0,0,0.5',
    fontColor: '255,255,255,1',
    activeBgColor: '250,100,0,1',
    activeFontColor: '255,255,255,1',
    animationType: 0,
  });
  useGate($GateGroupStyle, state);

  const handleConfirm = () => {
    $sceneMapApi.updateScene({
      id: sceneId,
      updater: (draft) => {
        draft.groups ??= {};
        draft.groups.bgColor = state.bgColor;
        draft.groups.fontColor = state.fontColor;
        draft.groups.activeBgColor = state.activeBgColor;
        draft.groups.activeFontColor = state.activeFontColor;
        draft.groups.animationType = state.animationType;
      },
    });
    onRequestClose();
  };

  const renderColorPicker = (key: keyof StyleData, name: string) => {
    return (
      <ColorPicker
        className="mb-20 last:mb-0"
        colorString={state[key]}
        onChange={(str) =>
          setState((draft) => {
            draft[key] = str;
          })
        }
      >
        <ColorPicker.Header>
          <div>{name}</div>
          <ColorPicker.Reset defaultColor={defaultState[key]!} />
        </ColorPicker.Header>
        <ColorPicker.Bar />
      </ColorPicker>
    );
  };

  return (
    <PanelBase
      isChanged={isChanged}
      title={<Title>分组高级设置</Title>}
      onRequestClose={onRequestClose}
      onConfirm={handleConfirm}
    >
      <Collapse title="样式设置">
        {renderColorPicker('bgColor', '默认背景颜色')}
        {renderColorPicker('fontColor', '默认字体颜色')}
        {renderColorPicker('activeBgColor', '选中背景颜色')}
        {renderColorPicker('activeFontColor', '选中字体颜色')}
      </Collapse>
      <Collapse title="分组切换动画">
        <Dropdown
          items={items}
          selected={state.animationType ?? 0}
          onChange={(item) =>
            setState((draft) => {
              draft.animationType = item;
            })
          }
        />
      </Collapse>
    </PanelBase>
  );
}
